import React, { useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import './MoveEmailsToFolderModal.css'

export type MoveEmailsFolderRow = { id: string; name: string }

type MoveEmailsToFolderModalProps = {
  show: boolean
  folders: MoveEmailsFolderRow[]
  onHide: () => void
  onConfirm: (folderId: string) => void
}

function MoveEmailsToFolderModal({
  show,
  folders,
  onHide,
  onConfirm,
}: MoveEmailsToFolderModalProps): React.ReactElement {
  const [search, setSearch] = useState('')
  const [highlightedId, setHighlightedId] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return folders
    return folders.filter((f) => f.name.toLowerCase().includes(q))
  }, [folders, search])

  useEffect(() => {
    if (!show) {
      setSearch('')
      setHighlightedId(null)
      return
    }
    setHighlightedId((prev) =>
      prev && folders.some((f) => f.id === prev)
        ? prev
        : (folders[0]?.id ?? null),
    )
  }, [show, folders])

  useEffect(() => {
    if (!show) return
    setHighlightedId((prev) => {
      if (filtered.some((f) => f.id === prev)) return prev
      return filtered[0]?.id ?? null
    })
  }, [filtered, show])

  const handleMoveHere = (folderId: string): void => {
    onConfirm(folderId)
  }

  const handleDone = (): void => {
    if (!highlightedId) return
    handleMoveHere(highlightedId)
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdropClassName="mce-move-folder-modal-backdrop"
      dialogClassName="mce-move-folder-modal-dialog"
      contentClassName="mce-move-folder-modal-content"
    >
      <div className="mce-move-folder-modal">
        <div className="mce-move-folder-head">
          <h2 className="mce-move-folder-title">Move emails</h2>
          <button
            type="button"
            className="mce-move-folder-close"
            aria-label="Close"
            onClick={onHide}
          >
            <i className="mgc mgc_close_line" aria-hidden />
          </button>
        </div>

        <div className="mce-move-folder-body">
          <label
            className="mce-move-folder-search-label"
            htmlFor="mce-move-folder-search"
          >
            Search folder
          </label>
          <input
            id="mce-move-folder-search"
            type="search"
            className="mce-move-folder-search-input"
            placeholder="Search folder name here"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />

          <div className="mce-move-folder-list">
            {filtered.length === 0 ? (
              <div className="mce-move-folder-empty">
                No folders match your search.
              </div>
            ) : (
              filtered.map((f) => {
                const active = highlightedId === f.id
                return (
                  <div
                    key={f.id}
                    className={`mce-move-folder-row${active ? ' mce-move-folder-row--active' : ''}`}
                  >
                    <button
                      type="button"
                      className="mce-move-folder-row-main"
                      onClick={() => setHighlightedId(f.id)}
                    >
                      <i
                        className="mgc mgc_folder_fill mce-move-folder-row-icon"
                        aria-hidden
                      />
                      <span className="mce-move-folder-row-name">{f.name}</span>
                    </button>
                    {active ? (
                      <button
                        type="button"
                        className="mce-move-folder-move-here"
                        onClick={() => handleMoveHere(f.id)}
                      >
                        Move here
                      </button>
                    ) : (
                      <span
                        className="mce-move-folder-move-placeholder"
                        aria-hidden
                      />
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        <div className="mce-move-folder-footer">
          <button
            type="button"
            className="mce-move-folder-btn-cancel"
            onClick={onHide}
          >
            Cancel
          </button>
          <button
            type="button"
            className="mce-move-folder-btn-done"
            onClick={handleDone}
            disabled={!highlightedId || filtered.length === 0}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default MoveEmailsToFolderModal
